/**
 * Health reminders: water, medicines and meals in one list, the next one first. Each reminder has
 * its own times, on/off switch and alert style (sound / vibrate / silent); all repeat daily. A
 * medicine can come straight from a current prescription (name, dosage, food and the prescribed
 * times filled in). The optional water tracker logs glasses against a daily goal.
 *
 * The server sends the reminders as push notifications — to this browser while notifications are
 * allowed here, and to the EMediExp app on the patient's phone.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { describeError } from "../../api/errors";
import { listFamily } from "../../api/patient";
import {
  createReminder,
  deleteReminder,
  getWaterIntake,
  listMedicineSuggestions,
  listReminders,
  setWaterIntake,
  testReminder,
  updateReminder,
  type AlertStyle,
  type FoodRelation,
  type HealthReminder,
  type HealthReminderInput,
  type HealthReminderPatch,
  type MealType,
  type ReminderKind,
  type WaterIntake,
} from "../../api/reminders";
import { useAuth } from "../../auth/AuthContext";
import { isInWebView } from "../../bridge/webview";
import { EditIcon, IconButton, RowActions, TrashIcon } from "../../components/ui/IconButton";
import { Modal } from "../../components/ui/Modal";
import { pushOnHere, pushPermission, setPushOnHere, watchPushPermission, type PushPermission } from "../../lib/push";
import {
  ALERT_STYLES,
  FOOD_LABELS,
  KIND_META,
  MEALS,
  SLOTS,
  WATER_INTERVALS,
  formatTime12,
  localDay,
  nextLabel,
  reminderDetail,
  reminderState,
  scheduleSummary,
} from "../../lib/reminders";
import { useNow } from "../../lib/useNow";

const remindersKey = (userId: string | undefined) => ["health-reminders", userId] as const;

export function Reminders() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const key = remindersKey(user?.id);
  // Refetched each minute, so a reminder that just went off shows its next time.
  const { data: reminders = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: listReminders,
    refetchInterval: 60_000,
  });
  const now = useNow();
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<HealthReminder | null>(null);
  const [removing, setRemoving] = useState<HealthReminder | null>(null);

  const toggle = useMutation({
    mutationFn: (r: HealthReminder) => updateReminder(r.id, { enabled: !r.enabled }),
    onSuccess: invalidate,
    onError: (err) => toast.error(describeError(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteReminder(id),
    onSuccess: () => {
      setRemoving(null);
      invalidate();
    },
    onError: (err) => toast.error(describeError(err)),
  });
  const test = useMutation({
    mutationFn: (id: string) => testReminder(id),
    onSuccess: (res) => {
      if (res.push_disabled) toast.warning("Notifications are switched off for your account. Turn them on in your profile.");
      else if (res.reminders_off)
        toast.warning("Reminder notifications are switched off. Turn on Push for Reminders in Settings → Notification preferences.");
      else if (res.devices === 0)
        toast.warning("No device gets notifications yet. Allow them in this browser, or sign in to the EMediExp app.");
      else if (res.sent === 0) toast.error("The test couldn't be delivered. Try again in a moment.");
      else toast.success(`Test sent to ${res.sent} device${res.sent === 1 ? "" : "s"}. Check that it arrived.`);
    },
    onError: (err) => toast.error(describeError(err)),
  });

  const water = reminders.find((r) => r.kind === "water") ?? null;

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Reminders</h2>
          <p className="text-sm text-gray-500">Water, medicines and meals, every day at the times you choose.</p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="btn-primary"
        >
          + Add reminder
        </button>
      </div>

      <NotificationStatus />

      <WaterTracker water={water} />

      <section className="space-y-3" aria-label="Your reminders">
        {isLoading ? (
          [0, 1, 2].map((i) => <div key={i} className="skeleton h-24 rounded-2xl" />)
        ) : reminders.length === 0 ? (
          <div className="empty-state">
            <p className="font-medium text-ink">No reminders yet</p>
            <p className="text-sm text-ink-muted">
              Add one for your medicines, for drinking water or for your meals.
            </p>
          </div>
        ) : (
          reminders.map((r) => (
            <ReminderRow
              key={r.id}
              reminder={r}
              now={now}
              busy={toggle.isPending && toggle.variables?.id === r.id}
              onToggle={() => toggle.mutate(r)}
              onEdit={() => {
                setEditing(r);
                setFormOpen(true);
              }}
              onTest={() => test.mutate(r.id)}
              onRemove={() => setRemoving(r)}
            />
          ))
        )}
      </section>

      <Modal
        open={formOpen}
        title={editing ? `Edit ${KIND_META[editing.kind].label.toLowerCase()} reminder` : "Add a reminder"}
        onClose={() => setFormOpen(false)}
        size="lg"
      >
        <ReminderForm
          initial={editing}
          existing={reminders}
          onCancel={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            invalidate();
          }}
        />
      </Modal>

      <Modal open={removing !== null} title="Delete reminder?" onClose={() => setRemoving(null)}>
        {removing && (
          <div className="space-y-4">
            <p className="text-sm text-ink-soft">
              “{removing.title}” stops and is removed. To pause it instead, switch it off.
            </p>
            <div className="flex gap-2">
              <button className="btn-danger" disabled={remove.isPending} onClick={() => remove.mutate(removing.id)}>
                {remove.isPending ? "Deleting…" : "Delete"}
              </button>
              <button className="btn-secondary" onClick={() => setRemoving(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function ReminderRow({
  reminder: r,
  now,
  busy,
  onToggle,
  onEdit,
  onTest,
  onRemove,
}: {
  reminder: HealthReminder;
  now: number;
  busy: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onTest: () => void;
  onRemove: () => void;
}) {
  const state = reminderState(r);
  const detail = reminderDetail(r);
  const style = ALERT_STYLES.find((a) => a.style === r.alert_style);
  return (
    <div className={`card flex items-start gap-3 p-4 ${state === "on" ? "" : "opacity-70"}`}>
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-50 text-xl" aria-hidden="true">
        {KIND_META[r.kind].icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-ink">{r.title}</span>
          {state === "off" && <span className="badge-neutral">Off</span>}
          {state === "ended" && <span className="badge-neutral">Course ended</span>}
        </div>
        {detail && <div className="text-sm text-ink-soft">{detail}</div>}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
          <span>{scheduleSummary(r)}</span>
          {style && (
            <span title={style.hint}>
              {style.icon} {style.label}
            </span>
          )}
          {r.end_date && <span>Until {new Date(`${r.end_date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>}
        </div>
        {state === "on" && r.next_fire_at && (
          <div className="mt-1 text-xs font-medium text-brand">Next: {nextLabel(r.next_fire_at, new Date(now))}</div>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        <Switch checked={r.enabled} disabled={busy} label={`${r.title} reminder`} onChange={onToggle} />
        <RowActions>
          <IconButton label="Send a test notification" onClick={onTest}>
            <BellRingIcon />
          </IconButton>
          <IconButton label="Edit" tone="brand" onClick={onEdit}>
            <EditIcon />
          </IconButton>
          <IconButton label="Delete" tone="danger" onClick={onRemove}>
            <TrashIcon />
          </IconButton>
        </RowActions>
      </div>
    </div>
  );
}

/** Whether this browser will show the reminders, with the one action that fixes it if not. */
function NotificationStatus() {
  const [permission, setPermission] = useState<PushPermission | null>(null);
  const [onHere, setOnHere] = useState(pushOnHere);
  useEffect(() => {
    let alive = true;
    pushPermission().then((p) => alive && setPermission(p));
    const unwatch = watchPushPermission((p) => setPermission(p));
    return () => {
      alive = false;
      unwatch();
    };
  }, []);

  if (isInWebView() || permission === null) return null; // the app shell shows its own
  if (permission === "granted" && onHere) {
    return (
      <p className="text-xs text-ink-muted">
        🔔 Notifications are on in this browser. Reminders also reach the EMediExp app on your phone.
      </p>
    );
  }
  const turnOn = async () => {
    const result = await setPushOnHere(true);
    setOnHere(true);
    setPermission(result);
    if (result === "denied") toast.error("Notifications are blocked for this site in your browser settings.");
  };
  const message =
    permission === "unsupported" || permission === "unconfigured"
      ? "This browser can't show notifications. Use the EMediExp app on your phone to get your reminders."
      : permission === "denied"
        ? "Notifications are blocked for this site. Allow them in your browser's site settings to get reminders here."
        : "Turn on notifications to get your reminders in this browser.";
  const canAsk = permission === "default" || (permission === "granted" && !onHere);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-ink">
      <span>{message}</span>
      {canAsk && (
        <button type="button" onClick={() => void turnOn()} className="btn-secondary btn-sm">
          Turn on notifications
        </button>
      )}
    </div>
  );
}

/** Optional daily intake log: glasses against the water reminder's goal, last 7 days. */
function WaterTracker({ water }: { water: HealthReminder | null }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const today = localDay();
  const weekAgo = localDay(new Date(Date.now() - 6 * 86_400_000));
  const key = ["water-intake", user?.id, today] as const;
  const { data } = useQuery({ queryKey: key, queryFn: () => getWaterIntake({ from: weekAgo, to: today }) });

  const glass = data?.glass_ml ?? 250;
  const goal = water?.daily_goal_ml ?? data?.goal_ml ?? 2000;
  const savedMl = data?.days.find((d) => d.day === today)?.amount_ml ?? 0;
  // The total the latest tap asked for, until the server has it. Kept in a ref as well, so taps
  // quicker than a render each build on the one before instead of on the last render's total.
  const [pending, setPending] = useState<number | null>(null);
  const pendingRef = useRef<number | null>(null);
  const settle = (value: number | null) => {
    pendingRef.current = value;
    setPending(value);
  };
  const todayMl = pending ?? savedMl;

  // One write at a time (scope), each carrying the day's total: they land in the order tapped.
  const save = useMutation({
    scope: { id: "water-intake" },
    mutationFn: (amount: number) => setWaterIntake(today, amount),
    onSuccess: (saved) => {
      qc.setQueryData<WaterIntake>(key, (old) =>
        old ? { ...old, days: [...old.days.filter((d) => d.day !== today), saved] } : old,
      );
      if (pendingRef.current === saved.amount_ml) settle(null);
    },
    onError: (err) => {
      settle(null);
      qc.invalidateQueries({ queryKey: key });
      toast.error(describeError(err));
    },
  });
  const change = (deltaMl: number) => {
    const next = Math.max(0, (pendingRef.current ?? savedMl) + deltaMl);
    settle(next);
    save.mutate(next);
  };

  const glasses = Math.round(todayMl / glass);
  const goalGlasses = Math.max(1, Math.round(goal / glass));
  const pct = Math.min(100, Math.round((todayMl / goal) * 100));
  const week = useMemo(() => {
    const out: { day: string; label: string; ml: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86_400_000);
      const day = localDay(d);
      out.push({
        day,
        label: d.toLocaleDateString("en-IN", { weekday: "narrow" }),
        ml: data?.days.find((x) => x.day === day)?.amount_ml ?? 0,
      });
    }
    return out;
  }, [data]);

  return (
    <section className="card p-4" aria-label="Water today">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-info-soft text-xl" aria-hidden="true">
            💧
          </div>
          <div>
            <div className="text-sm text-ink-muted">Water today</div>
            <div className="text-lg font-semibold text-ink">
              {glasses} of {goalGlasses} glasses
              <span className="ml-2 text-sm font-normal text-ink-muted">
                {(todayMl / 1000).toFixed(2).replace(/\.?0+$/, "")} L of {goal / 1000} L
              </span>
              <span className="ml-2 text-xs font-normal text-ink-faint" aria-live="polite">
                {pending !== null ? "Saving…" : ""}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={todayMl === 0}
            onClick={() => change(-glass)}
            aria-label="Remove a glass"
          >
            − Glass
          </button>
          <button type="button" className="btn-primary btn-sm" onClick={() => change(glass)} aria-label="Add a glass">
            + Glass
          </button>
        </div>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-sunken" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-info transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-3 flex items-end gap-2" aria-label="Last 7 days">
        {week.map((d) => (
          <div key={d.day} className="flex flex-1 flex-col items-center gap-1" title={`${d.day}: ${d.ml} ml`}>
            <div className="flex h-10 w-full items-end overflow-hidden rounded-md bg-sunken">
              <div className="w-full bg-info/70" style={{ height: `${Math.min(100, (d.ml / goal) * 100)}%` }} />
            </div>
            <span className={`text-[10px] ${d.day === today ? "font-semibold text-ink" : "text-ink-muted"}`}>{d.label}</span>
          </div>
        ))}
      </div>
      {!water && (
        <p className="mt-3 text-xs text-ink-muted">Tip: add a water reminder to be nudged through the day.</p>
      )}
    </section>
  );
}

type FormState = {
  kind: ReminderKind;
  title: string;
  dosage: string;
  food_relation: FoodRelation | "";
  for_family_member_id: string;
  meal_type: MealType | "";
  times: string[];
  interval_minutes: 30 | 60 | 120;
  window_start: string;
  window_end: string;
  start_date: string;
  end_date: string;
  daily_goal_ml: string;
  note: string;
  enabled: boolean;
  alert_style: AlertStyle;
};

function initialForm(r: HealthReminder | null, existing: HealthReminder[]): FormState {
  const freeMeal = MEALS.find((m) => !existing.some((x) => x.kind === "meal" && x.meal_type === m.type));
  return {
    kind: r?.kind ?? "medicine",
    title: r?.title ?? "",
    dosage: r?.dosage ?? "",
    food_relation: r?.food_relation ?? "",
    for_family_member_id: r?.for_family_member_id ?? "",
    meal_type: r?.meal_type ?? freeMeal?.type ?? "",
    times: r?.times ?? ["08:00"],
    interval_minutes: r?.interval_minutes ?? 60,
    window_start: r?.window_start ?? "08:00",
    window_end: r?.window_end ?? "22:00",
    start_date: r?.start_date ?? "",
    end_date: r?.end_date ?? "",
    daily_goal_ml: r?.daily_goal_ml ? String(r.daily_goal_ml) : "2000",
    note: r?.note ?? "",
    enabled: r?.enabled ?? true,
    alert_style: r?.alert_style ?? "sound",
  };
}

function ReminderForm({
  initial,
  existing,
  onSaved,
  onCancel,
}: {
  initial: HealthReminder | null;
  existing: HealthReminder[];
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<FormState>(() => initialForm(initial, existing));
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));
  const creating = initial === null;
  const hasWater = existing.some((r) => r.kind === "water");
  const takenMeals = new Set(existing.filter((r) => r.kind === "meal").map((r) => r.meal_type));

  const { data: family = [] } = useQuery({ queryKey: ["family"], queryFn: listFamily, enabled: form.kind === "medicine" });

  const body = (): HealthReminderInput => {
    const common = { note: form.note.trim() || null, enabled: form.enabled, alert_style: form.alert_style };
    if (form.kind === "water") {
      return {
        kind: "water",
        ...common,
        interval_minutes: form.interval_minutes,
        window_start: form.window_start,
        window_end: form.window_end,
        daily_goal_ml: Number(form.daily_goal_ml) || null,
      };
    }
    if (form.kind === "meal") {
      return { kind: "meal", ...common, meal_type: form.meal_type || null, times: form.times.slice(0, 1) };
    }
    return {
      kind: "medicine",
      ...common,
      title: form.title.trim(),
      dosage: form.dosage.trim() || null,
      food_relation: form.food_relation || null,
      for_family_member_id: form.for_family_member_id || null,
      times: [...new Set(form.times)].sort(),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    };
  };

  const save = useMutation({
    mutationFn: () => {
      const b = body();
      if (creating) return createReminder(b);
      // A reminder's kind and meal can't change; everything else is sent as edited.
      const patch = Object.fromEntries(
        Object.entries(b).filter(([field]) => field !== "kind" && field !== "meal_type"),
      ) as HealthReminderPatch;
      return updateReminder(initial.id, patch);
    },
    onSuccess: () => {
      toast.success(creating ? "Reminder added." : "Reminder saved.");
      onSaved();
    },
    onError: (err) => setError(describeError(err)),
  });

  const setKind = (kind: ReminderKind) => {
    const meal = MEALS.find((m) => m.type === form.meal_type) ?? MEALS[0];
    set({ kind, times: kind === "meal" ? [meal.time] : kind === "medicine" ? ["08:00"] : form.times });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (form.kind === "medicine" && form.times.length === 0) {
          setError("Choose at least one time to take the medicine.");
          return;
        }
        save.mutate();
      }}
      className="space-y-5"
    >
      {creating && (
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Reminder type">
          {(Object.keys(KIND_META) as ReminderKind[]).map((k) => {
            const disabled = k === "water" && hasWater;
            return (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={form.kind === k}
                disabled={disabled}
                title={disabled ? "You already have a water reminder. Edit that one." : undefined}
                onClick={() => setKind(k)}
                className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition disabled:opacity-40 ${
                  form.kind === k ? "border-brand bg-brand-50 text-brand" : "border-line hover:border-brand-200"
                }`}
              >
                <span className="mr-1" aria-hidden="true">{KIND_META[k].icon}</span>
                {KIND_META[k].label}
              </button>
            );
          })}
        </div>
      )}

      {form.kind === "medicine" && creating && <PrescriptionSuggestions onAdded={onSaved} />}

      {form.kind === "medicine" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Medicine name">
            <input value={form.title} onChange={(e) => set({ title: e.target.value })} required maxLength={80} className="input" placeholder="e.g. Metformin 500" />
          </Field>
          <Field label="Dosage (optional)">
            <input value={form.dosage} onChange={(e) => set({ dosage: e.target.value })} maxLength={60} className="input" placeholder="e.g. 1 tablet" />
          </Field>
          <Field label="With food">
            <select value={form.food_relation} onChange={(e) => set({ food_relation: e.target.value as FoodRelation | "" })} className="select">
              <option value="">Doesn't matter</option>
              {(Object.keys(FOOD_LABELS) as FoodRelation[]).map((f) => (
                <option key={f} value={f}>{FOOD_LABELS[f]}</option>
              ))}
            </select>
          </Field>
          <Field label="For">
            <select value={form.for_family_member_id} onChange={(e) => set({ for_family_member_id: e.target.value })} className="select">
              <option value="">Myself</option>
              {family.map((m) => (
                <option key={m.id} value={m.id}>{m.full_name}</option>
              ))}
            </select>
          </Field>
        </div>
      )}

      {form.kind === "meal" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Meal">
            <select
              value={form.meal_type}
              disabled={!creating}
              onChange={(e) => {
                const meal = MEALS.find((m) => m.type === e.target.value);
                set({ meal_type: e.target.value as MealType, times: meal ? [meal.time] : form.times });
              }}
              className="select"
              required
            >
              {MEALS.map((m) => (
                <option key={m.type} value={m.type} disabled={creating && takenMeals.has(m.type)}>
                  {m.label}{creating && takenMeals.has(m.type) ? " (already set)" : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Time">
            <input type="time" value={form.times[0] ?? ""} onChange={(e) => set({ times: [e.target.value] })} required className="input" />
          </Field>
        </div>
      )}

      {form.kind === "medicine" && <MedicineTimes times={form.times} onChange={(times) => set({ times })} />}

      {form.kind === "medicine" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="From (optional)">
            <input type="date" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} className="input" />
          </Field>
          <Field label="Until (optional)">
            <input type="date" value={form.end_date} min={form.start_date || undefined} onChange={(e) => set({ end_date: e.target.value })} className="input" />
          </Field>
        </div>
      )}

      {form.kind === "water" && (
        <div className="space-y-3">
          <div>
            <span className="label">Remind me every</span>
            <div className="flex gap-2" role="radiogroup" aria-label="Interval">
              {WATER_INTERVALS.map((w) => (
                <button
                  key={w.minutes}
                  type="button"
                  role="radio"
                  aria-checked={form.interval_minutes === w.minutes}
                  onClick={() => set({ interval_minutes: w.minutes })}
                  className={`rounded-xl border px-3 py-2 text-sm font-medium ${
                    form.interval_minutes === w.minutes ? "border-brand bg-brand-50 text-brand" : "border-line"
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="From">
              <input type="time" value={form.window_start} onChange={(e) => set({ window_start: e.target.value })} required className="input" />
            </Field>
            <Field label="Until">
              <input type="time" value={form.window_end} onChange={(e) => set({ window_end: e.target.value })} required className="input" />
            </Field>
            <Field label="Daily goal (ml)">
              <input
                type="number"
                min={500}
                max={6000}
                step={250}
                value={form.daily_goal_ml}
                onChange={(e) => set({ daily_goal_ml: e.target.value })}
                className="input"
              />
            </Field>
          </div>
        </div>
      )}

      <Field label="Note (optional)">
        <input value={form.note} onChange={(e) => set({ note: e.target.value })} maxLength={200} className="input" placeholder="Shown in the notification" />
      </Field>

      <fieldset>
        <legend className="label">Notification</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {ALERT_STYLES.map((a) => (
            <label
              key={a.style}
              className={`flex cursor-pointer items-start gap-2 rounded-xl border p-3 text-sm ${
                form.alert_style === a.style ? "border-brand bg-brand-50" : "border-line"
              }`}
            >
              <input
                type="radio"
                name="alert_style"
                className="mt-0.5"
                checked={form.alert_style === a.style}
                onChange={() => set({ alert_style: a.style })}
              />
              <span>
                <span className="font-medium">{a.icon} {a.label}</span>
                <span className="block text-xs text-ink-muted">{a.hint}</span>
              </span>
            </label>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
          Reminder on (repeats every day)
        </label>
      </fieldset>

      {error && <p className="field-error">{error}</p>}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={save.isPending}>
          {save.isPending ? "Saving…" : creating ? "Add reminder" : "Save"}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Morning / Afternoon / Evening / Night toggles, each at its default time, plus exact times. */
function MedicineTimes({ times, onChange }: { times: string[]; onChange: (times: string[]) => void }) {
  // Kept in the order added while editing (sorted on save): re-sorting on each change would move
  // the time being typed into another input.
  const toggleSlot = (time: string) =>
    onChange(times.includes(time) ? times.filter((t) => t !== time) : [...times, time]);
  return (
    <div>
      <span className="label">When to take it</span>
      <div className="flex flex-wrap gap-2">
        {SLOTS.map((s) => {
          const on = times.includes(s.time);
          return (
            <button
              key={s.key}
              type="button"
              aria-pressed={on}
              onClick={() => toggleSlot(s.time)}
              className={`rounded-full border px-3 py-1.5 text-sm ${on ? "border-brand bg-brand-50 text-brand" : "border-line"}`}
            >
              {s.label} · {formatTime12(s.time)}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {times.map((t, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1">
            <input
              type="time"
              value={t}
              aria-label="Reminder time"
              onChange={(e) => onChange(times.map((x, j) => (j === i ? e.target.value : x)).filter(Boolean))}
              className="border-0 bg-transparent p-0 text-sm focus:ring-0"
            />
            <button type="button" aria-label="Remove this time" onClick={() => onChange(times.filter((_, j) => j !== i))} className="text-ink-muted hover:text-danger">
              ×
            </button>
          </span>
        ))}
        {times.length < 6 && (
          <button type="button" className="btn-ghost btn-sm" onClick={() => onChange([...times, "12:00"])}>
            + Add a time
          </button>
        )}
      </div>
    </div>
  );
}

/** Medicines on the patient's current prescriptions: one tap makes a reminder with the
    prescribed name, dosage, food relation, times and course length. */
function PrescriptionSuggestions({ onAdded }: { onAdded: () => void }) {
  const { data = [], isLoading } = useQuery({ queryKey: ["medicine-suggestions"], queryFn: listMedicineSuggestions });
  const [showAll, setShowAll] = useState(false);
  const qc = useQueryClient();
  const add = useMutation({
    mutationFn: (lineId: string) => createReminder({ kind: "medicine", prescription_line_id: lineId }),
    onSuccess: (r) => {
      toast.success(`Reminder added for ${r.title}.`);
      qc.invalidateQueries({ queryKey: ["medicine-suggestions"] });
      onAdded();
    },
    onError: (err) => toast.error(describeError(err)),
  });
  if (isLoading || data.length === 0) return null;
  // Not yet added first; a few at a time, so the form stays in view.
  const ordered = [...data].sort((a, b) => Number(a.already_added) - Number(b.already_added));
  const shown = showAll ? ordered : ordered.slice(0, SUGGESTIONS_SHOWN);
  return (
    <div className="rounded-xl border border-line bg-raised p-3">
      <div className="section-title mb-2">From your prescriptions</div>
      <ul className="space-y-2">
        {shown.map((s) => (
          <li key={s.prescription_line_id} className="flex items-center justify-between gap-3">
            <div className="min-w-0 text-sm">
              <div className="truncate font-medium text-ink">{s.medicine_name}</div>
              <div className="text-xs text-ink-muted">
                {[s.dosage, FOOD_LABELS[s.food_relation]?.toLowerCase(), s.times.map(formatTime12).join(", "), s.for_name ? `for ${s.for_name}` : null, s.prescription_code]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <button
              type="button"
              className="btn-secondary btn-sm shrink-0"
              disabled={s.already_added || add.isPending}
              onClick={() => add.mutate(s.prescription_line_id)}
            >
              {s.already_added ? "Added" : "Add"}
            </button>
          </li>
        ))}
      </ul>
      {ordered.length > SUGGESTIONS_SHOWN && (
        <button type="button" className="link mt-2 text-xs" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show fewer" : `Show all ${ordered.length}`}
        </button>
      )}
      <p className="mt-2 text-xs text-ink-muted">Or set one up yourself below.</p>
    </div>
  );
}

const SUGGESTIONS_SHOWN = 4;

function Switch({ checked, disabled, label, onChange }: { checked: boolean; disabled?: boolean; label: string; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={`flex h-6 w-11 items-center rounded-full p-0.5 transition disabled:opacity-50 ${checked ? "bg-brand" : "bg-line-strong"}`}
    >
      <span className={`h-5 w-5 rounded-full bg-white shadow-xs transition ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

function BellRingIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-full w-full">
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
      <path d="M2 8c0-2.2.7-4.3 2-6M22 8c0-2.2-.7-4.3-2-6" />
    </svg>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="label mb-0">{label}</span>
      {children}
    </label>
  );
}

